import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogContentText,
  DialogProps,
  DialogTitle,
} from "@mui/material";
import React from "react";
import { ColorMeaning, GetDialogColorByMeaning } from "./ColorMeaning";
import { DialogEventArgs } from "./DialogEventArgs";
import { useTranslation } from "react-i18next";

export type DialogBaseUIProps = {
  title?: React.ReactNode;
  message?: React.ReactNode;
  content?: React.ReactNode;

  colorMeaning?: ColorMeaning;

  /** 原始順序為 cancel/yes 遇到危險動作順序改為 danger/cancel */
  isDangerAction?: boolean;

  dialogContentProps?: DialogContentProps;

  yesText?: string;
  noText?: string;
  cancelText?: string;

  yesButtonProps?: ButtonProps;
  noButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
};

export type DialogBaseEventProps = {
  onDialogResult?: (e: DialogEventArgs) => void;
};

export type DialogBaseProps = DialogProps &
  DialogBaseUIProps &
  DialogBaseEventProps;

const DialogBase = ({
  title,
  message,
  content,
  dialogContentProps,
  colorMeaning,
  isDangerAction,
  yesText,
  noText,
  cancelText,
  yesButtonProps,
  noButtonProps,
  cancelButtonProps,

  onDialogResult,

  style,
  sx,

  ...props
}: DialogBaseProps) => {
  const dflt_sx = { minWidth: "80px" };
  const { t } = useTranslation();

  let actionButtons = [
    // 原生動作順序: cancel/no/yes(action)
    render_CancelButton(),
    render_NoButton(),
    render_YesButton(),
  ];

  if (isDangerAction) {
    // 危險的動作確認 按鈕順序: yes(danger)/no/cancel
    actionButtons = actionButtons.slice(0).reverse();
  }

  const theme_color = GetDialogColorByMeaning(colorMeaning);

  return (
    <Dialog
      {...props}
      onClose={() => {
        onDialogResult?.(new DialogEventArgs(undefined, "Cancel", undefined));
      }}
    >
      {render_ColorMeaningHeader()}
      <Box
        style={{
          ...{
            paddingTop: "8px",
            paddingLeft: "0px",
            paddingRight: "0px",
            paddingBottom: "8px",
          },
          ...style,
        }}
        sx={{ ...sx }}
      >
        {render_DialogTitle()}
        {render_Message()}
        {render_DialogContent()}

        {render_DialogActions()}
      </Box>
      {render_ColorMeaningFooter()}
    </Dialog>
  );

  function render_YesButton(): React.ReactNode {
    return yesText == null ? undefined : (
      <Button
        key="btn_Yes"
        {...yesButtonProps}
        sx={{ ...dflt_sx, ...yesButtonProps?.sx }}
        onClick={() => {
          onDialogResult?.(new DialogEventArgs(undefined, "Yes", undefined));
        }}
      >
        {t(yesText)}
      </Button>
    );
  }

  function render_NoButton(): React.ReactNode {
    return noText == null ? undefined : (
      <Button
        key="btn_No"
        {...noButtonProps}
        sx={{ ...dflt_sx, ...noButtonProps?.sx }}
        onClick={() => {
          onDialogResult?.(new DialogEventArgs(undefined, "No", undefined));
        }}
      >
        {t(noText)}
      </Button>
    );
  }

  function render_CancelButton(): React.ReactNode {
    return cancelText == null ? undefined : (
      <Button
        key="btn_Cancel"
        {...cancelButtonProps}
        sx={{ ...dflt_sx, ...cancelButtonProps?.sx }}
        onClick={() => {
          onDialogResult?.(new DialogEventArgs(undefined, "Cancel", undefined));
        }}
      >
        {t(cancelText)}
      </Button>
    );
  }

  function render_ColorMeaningHeader(): React.ReactNode {
    if (colorMeaning == null) {
      return undefined;
    }

    return (
      <Box sx={{ width: "100%", height: "4px", bgcolor: theme_color }}></Box>
    );
  }

  function render_ColorMeaningFooter(): React.ReactNode {
    if (colorMeaning == null) {
      return undefined;
    }

    return (
      <Box sx={{ width: "100%", height: "2px", bgcolor: theme_color }}></Box>
    );
  }

  function render_DialogTitle(): React.ReactNode {
    if (title == null && message == null) {
      return undefined;
    }

    return (
      <DialogTitle color={theme_color} whiteSpace="pre-wrap">
        {title}
      </DialogTitle>
    );
  }

  function render_DialogContent(): React.ReactNode {
    if (content == null) {
      return undefined;
    }

    return (
      <div>
        <DialogContent {...dialogContentProps}>
          {render_Content()}
        </DialogContent>
      </div>
    );
  }

  function render_Message(): React.ReactNode {
    if (message == null) {
      return undefined;
    }

    return (
      <DialogContentText
        whiteSpace="pre-wrap"
        maxHeight={"400px"}
        sx={{ paddingX: "24px", paddingBottom: "8px", overflowY: "auto" }}
      >
        {message}
      </DialogContentText>
    );
  }

  function render_Content(): React.ReactNode {
    if (content == null) {
      return undefined;
    }

    return <>{content}</>;
  }

  function render_DialogActions(): React.ReactNode {
    if (yesText == null && noText == null && cancelText == null) {
      return undefined;
    }

    return (
      <DialogActions sx={{ px: 3 }}>
        {actionButtons.map((item) => {
          return item;
        })}
      </DialogActions>
    );
  }
};

export default DialogBase;
